/**
 * @module use-velociraptor-config-save
 * @purpose Hook encapsulating the save/save-as logic for VelociraptorConfigModal
 * @depends useArtefactTemplateMutations, template-permissions utils
 * @dependedOnBy VelociraptorConfigModal
 *
 * @rules
 * - State: no local state; delegates to parent-provided mutations
 * - Errors: returns error string for display by parent
 */

import { useCallback } from 'react'
import { logger } from '../../utils/logger'
import type { ArtefactTemplate, ArtefactTemplateScope } from '../../types/artefact-templates'
import type { ArtefactConfig } from '../../types/velociraptor'
import type { useArtefactTemplateMutations } from '../../hooks/use-artefact-templates'

type ArtefactTemplateMutations = ReturnType<typeof useArtefactTemplateMutations>
import {
  getSaveAsTargetScope,
  type TemplateUserRole,
  type TemplateSaveAction,
} from '../../utils/template-permissions'

interface PlatformMutations {
  create: (input: {
    name: string
    description?: string
    artefacts: ArtefactConfig[]
    supportedPlatforms?: string[]
  }) => Promise<ArtefactTemplate | null>
  update: (
    templateId: string,
    input: {
      name?: string
      description?: string
      artefacts?: ArtefactConfig[]
      supportedPlatforms?: string[]
    }
  ) => Promise<ArtefactTemplate | null>
  creating: boolean
  updating: boolean
  error: string | null
}

interface SaveContext {
  scope: ArtefactTemplateScope
  caseId?: string | undefined
  userRole: TemplateUserRole
}

interface SaveInput {
  name: string
  description: string
  selectedArtefacts: string[]
  parameterOverrides: Record<string, Record<string, string | number | boolean>>
  platforms: string[]
}

interface UseSaveParams {
  saveAction: TemplateSaveAction
  editTemplate: ArtefactTemplate | null | undefined
  context: SaveContext
  mutations: ArtefactTemplateMutations
  platformMutations: PlatformMutations | undefined
  onTemplateSaved: (template: ArtefactTemplate) => void
  onClose: () => void
  setSaveError: (error: string | null) => void
}

type TemplateInput = {
  name: string
  description?: string
  artefacts: ArtefactConfig[]
  supportedPlatforms: string[]
}

interface ExecuteSaveArgs {
  saveAction: TemplateSaveAction
  editTemplate: ArtefactTemplate | null | undefined
  context: SaveContext
  mutations: ArtefactTemplateMutations
  platformMutations: PlatformMutations | undefined
  input: TemplateInput
}

interface ScopedMutationArgs {
  scope: ArtefactTemplateScope
  caseId: string | undefined
  mutations: ArtefactTemplateMutations
  platformMutations: PlatformMutations | undefined
  input: TemplateInput
}

async function updateExisting(
  templateId: string,
  args: ScopedMutationArgs
): Promise<ArtefactTemplate | null> {
  const { scope, caseId, mutations, platformMutations, input } = args
  if (scope === 'platform' && platformMutations) return platformMutations.update(templateId, input)
  if (scope === 'organisation') return mutations.updateOrg(templateId, input)
  if (caseId) return mutations.updateCase(caseId, templateId, input)
  return null
}

async function createNew(args: ScopedMutationArgs): Promise<ArtefactTemplate | null> {
  const { scope, caseId, mutations, platformMutations, input } = args
  if (scope === 'platform' && platformMutations) return platformMutations.create(input)
  if (scope === 'organisation') return mutations.createOrg(input)
  if (caseId) return mutations.createCase(caseId, input)
  return null
}

async function executeSave(args: ExecuteSaveArgs): Promise<ArtefactTemplate | null> {
  const { saveAction, editTemplate, context, mutations, platformMutations, input } = args
  const scoped: ScopedMutationArgs = {
    scope: context.scope,
    caseId: context.caseId,
    mutations,
    platformMutations,
    input,
  }
  if (saveAction === 'save' && editTemplate) {
    return updateExisting(editTemplate.id, scoped)
  }
  const targetScope = editTemplate ? getSaveAsTargetScope(context.userRole) : context.scope
  return createNew({ ...scoped, scope: targetScope })
}

export function useVelociraptorConfigSave({
  saveAction,
  editTemplate,
  context,
  mutations,
  platformMutations,
  onTemplateSaved,
  onClose,
  setSaveError,
}: UseSaveParams): (saveInput: SaveInput) => Promise<void> {
  return useCallback(
    async ({ name, description, selectedArtefacts, parameterOverrides, platforms }: SaveInput) => {
      setSaveError(null)

      const artefactConfigs: ArtefactConfig[] = selectedArtefacts.map((artName) => ({
        name: artName,
        parameters: parameterOverrides[artName] ?? {},
      }))

      const trimmedDescription = description.trim()
      const input = {
        name: name.trim(),
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        artefacts: artefactConfigs,
        supportedPlatforms: platforms,
      }

      const result = await executeSave({
        saveAction,
        editTemplate,
        context,
        mutations,
        platformMutations,
        input,
      })

      if (result) {
        if (saveAction === 'save' && editTemplate) {
          logger.info('VelociraptorConfigModal', 'Template updated', {
            templateId: editTemplate.id,
          })
        }
        onTemplateSaved(result)
        onClose()
        return
      }

      const mutationError =
        context.scope === 'platform' ? platformMutations?.error : mutations.error
      if (mutationError) setSaveError(mutationError)
    },
    [
      saveAction,
      editTemplate,
      context,
      mutations,
      platformMutations,
      onTemplateSaved,
      onClose,
      setSaveError,
    ]
  )
}
